import React, { useState, useEffect } from 'react';
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, 
  Loader2, MapPin, Clock, Users, Video, Mic, Star, X,
  Camera, Briefcase, Heart, CheckCircle2
} from 'lucide-react';
import { useUserEmail } from '../../context/UserAuthContext';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const EVENT_TYPES = {
  shoot: { label: 'Photo/Video Shoot', icon: Camera, color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  interview: { label: 'Interview', icon: Mic, color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  podcast: { label: 'Podcast', icon: Mic, color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  speaking: { label: 'Speaking', icon: Users, color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  meeting: { label: 'Meeting', icon: Video, color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
  community: { label: 'Community Event', icon: Heart, color: 'bg-pink-500/20 text-pink-400 border-pink-500/30' },
  partnership: { label: 'Partnership', icon: Briefcase, color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  content: { label: 'Content Deadline', icon: CheckCircle2, color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  game: { label: 'Game Day', icon: Star, color: 'bg-red-500/20 text-red-400 border-red-500/30' }
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const AECalendar = () => {
  const userEmail = useUserEmail();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [view, setView] = useState('month'); // month, week, agenda
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    if (userEmail) {
      fetchEvents();
    }
  }, [userEmail, currentDate]);

  const fetchEvents = async () => {
    try {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const res = await fetch(
        `${API}/api/always-elevate/calendar?user_email=${encodeURIComponent(userEmail)}&start_date=${startOfMonth.toISOString()}&end_date=${endOfMonth.toISOString()}`
      );
      const data = await res.json();
      if (data.success) {
        setEvents(data.data);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Previous month days
    const prevMonth = new Date(year, month, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthDays - i),
        isCurrentMonth: false
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const filteredEvents = events.filter(event => {
    if (filterType === 'all') return true;
    return event.event_type === filterType;
  });

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const days = getDaysInMonth(currentDate);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C4A052]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ae-calendar-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#C4A052] text-sm font-medium tracking-wider">ALWAYS ELEVATE</p>
              <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: 'Anton, sans-serif' }} data-testid="ae-calendar-title">
                Calendar
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {/* View Toggle */}
              <div className="flex items-center bg-white/5 rounded-lg p-1" data-testid="ae-calendar-view-toggle">
                {['month', 'agenda'].map(v => (
                  <button
                    key={v}
                    onClick={() => setView(v)}
                    className={`px-3 py-1.5 rounded text-sm capitalize transition-colors ${
                      view === v ? 'bg-[#C4A052] text-black' : 'text-gray-400 hover:text-white'
                    }`}
                    data-testid={`ae-calendar-view-${v}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => setFilterType('all')}
            className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
              filterType === 'all' ? 'bg-[#C4A052] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            All Events
          </button>
          {Object.entries(EVENT_TYPES).map(([key, { label, color }]) => (
            <button
              key={key}
              onClick={() => setFilterType(key)}
              className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                filterType === key ? 'bg-[#C4A052] text-black' : `${color.split(' ')[0]} ${color.split(' ')[1]}`
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {view === 'month' ? (
          <div className="bg-black border border-white/20 rounded-xl overflow-hidden">
            {/* Month Navigation */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-bold">
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Day Headers */}
            <div className="grid grid-cols-7 border-b border-white/10">
              {DAYS.map(day => (
                <div key={day} className="p-3 text-center text-sm font-medium text-gray-400">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7">
              {days.map((day, idx) => {
                const dayEvents = getEventsForDate(day.date).filter(e => 
                  filterType === 'all' || e.event_type === filterType
                );
                const isSelected = selectedDate?.toDateString() === day.date.toDateString();
                
                return (
                  <div
                    key={idx}
                    onClick={() => setSelectedDate(day.date)}
                    className={`min-h-[100px] p-2 border-b border-r border-white/5 cursor-pointer transition-colors ${
                      !day.isCurrentMonth ? 'bg-white/[0.02] text-gray-600' : 'hover:bg-white/5'
                    } ${isSelected ? 'bg-[#C4A052]/10' : ''} ${isToday(day.date) ? 'bg-[#C4A052]/5' : ''}`}
                  >
                    <div className={`text-sm font-medium mb-1 ${
                      isToday(day.date) ? 'text-[#C4A052]' : ''
                    }`}>
                      {day.date.getDate()}
                    </div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map((event, i) => {
                        const eventType = EVENT_TYPES[event.event_type] || EVENT_TYPES.meeting;
                        return (
                          <div
                            key={i}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedEvent(event);
                            }}
                            className={`text-xs px-1.5 py-0.5 rounded truncate border ${eventType.color}`}
                          >
                            {event.title}
                          </div>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <div className="text-xs text-gray-500 px-1">
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* Agenda View */
          <div className="space-y-4">
            {filteredEvents.length === 0 ? (
              <div className="bg-black border border-white/20 rounded-xl p-12 text-center">
                <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-lg font-medium text-gray-400 mb-2">No Events</h3>
                <p className="text-gray-500 text-sm">No events scheduled for this period</p>
              </div>
            ) : (
              filteredEvents
                .sort((a, b) => new Date(a.date) - new Date(b.date))
                .map(event => {
                  const eventType = EVENT_TYPES[event.event_type] || EVENT_TYPES.meeting;
                  const EventIcon = eventType.icon;
                  const eventDate = new Date(event.date);
                  
                  return (
                    <div
                      key={event.id}
                      onClick={() => setSelectedEvent(event)}
                      className={`bg-white/5 border rounded-xl p-4 cursor-pointer ${eventType.color.split(' ')[2] || 'border-white/10'}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="text-center min-w-[60px]">
                          <p className="text-xs text-gray-400 uppercase">
                            {MONTHS[eventDate.getMonth()].slice(0, 3)}
                          </p>
                          <p className="text-2xl font-bold text-[#C4A052]">
                            {eventDate.getDate()}
                          </p>
                          <p className="text-xs text-gray-500">
                            {DAYS[eventDate.getDay()]}
                          </p>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs px-2 py-0.5 rounded ${eventType.color}`}>
                              {eventType.label}
                            </span>
                          </div>
                          <h3 className="font-semibold mb-1">{event.title}</h3>
                          <div className="flex items-center gap-4 text-sm text-gray-400">
                            {event.location && (
                              <div className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                <span>{event.location}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              <span>{eventDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        )}
      </main>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-lg w-full">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {(() => {
                  const eventType = EVENT_TYPES[selectedEvent.event_type] || EVENT_TYPES.meeting;
                  const EventIcon = eventType.icon;
                  return (
                    <div className={`p-2 rounded-lg ${eventType.color}`}>
                      <EventIcon className="w-5 h-5" />
                    </div>
                  );
                })()}
                <h3 className="text-lg font-bold">{selectedEvent.title}</h3>
              </div>
              <button onClick={() => setSelectedEvent(null)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Date</p>
                  <p className="font-medium">{new Date(selectedEvent.date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    month: 'long', 
                    day: 'numeric',
                    year: 'numeric'
                  })}</p>
                </div>
                {selectedEvent.location && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Location</p>
                    <p className="font-medium">{selectedEvent.location}</p>
                  </div>
                )}
              </div>

              {selectedEvent.description && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Description</p>
                  <p className="text-gray-300">{selectedEvent.description}</p>
                </div>
              )}

              {selectedEvent.talking_points && selectedEvent.talking_points.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">Talking Points</p>
                  <ul className="space-y-1">
                    {selectedEvent.talking_points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                        <span className="text-[#C4A052]">•</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-white/10 flex justify-end gap-3">
              <button
                onClick={() => setSelectedEvent(null)}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                Close
              </button>
              <button className="px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors">
                Add to Calendar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AECalendar;
